import { useEffect, useState } from 'react';
import {
  clearIdentityConfigCache,
  ensureAppleReady,
  fetchIdentityConfig,
  signInWithApple,
} from '@/lib/social-auth';
import GoogleAuthButton from '@/components/GoogleAuthButton';

const PROVIDER_LABELS = { google: 'Google', apple: 'Apple' };

export default function SocialAuthButtons({
  mode = 'auth',
  intent = 'login',
  disabled = false,
  onProviderToken,
  className = '',
  showDivider = true,
}) {
  const [providers, setProviders] = useState([]);
  const [googleClientId, setGoogleClientId] = useState('');
  const [appleClientId, setAppleClientId] = useState('');
  const [appleBusy, setAppleBusy] = useState(false);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    let cancel = false;
    clearIdentityConfigCache();
    (async () => {
      try {
        const cfg = await fetchIdentityConfig({ bustCache: true });
        if (cancel) return;
        const list = Array.isArray(cfg.providers) ? cfg.providers : [];
        setProviders(list);
        setGoogleClientId(cfg.googleClientId || '');
        setAppleClientId(cfg.appleClientId || '');
        if (cfg.appleClientId) {
          await ensureAppleReady(cfg.appleClientId);
        }
      } catch (err) {
        if (!cancel) setLoadError(err?.message || 'Social sign-in unavailable');
      }
    })();
    return () => {
      cancel = true;
    };
  }, [disabled, onProviderToken]);

  const handleApple = async () => {
    if (disabled || appleBusy) return;
    setAppleBusy(true);
    try {
      const token = await signInWithApple();
      onProviderToken?.('apple', token);
    } catch (err) {
      setLoadError(err?.message || 'Apple sign-in failed');
    } finally {
      setAppleBusy(false);
    }
  };

  if (!providers.length && !loadError) return null;

  return (
    <div className={`space-y-3 ${className}`.trim()} data-testid="social-auth-buttons">
      {loadError ? (
        <div
          className="rounded-lg border border-[var(--error)]/40 bg-[var(--error)]/10 px-3 py-2 text-xs text-[var(--text-primary)]"
          role="alert"
        >
          {loadError}
        </div>
      ) : null}
      {providers.includes('google') ? (
        <GoogleAuthButton
          clientId={googleClientId}
          intent={intent}
          disabled={disabled}
          onToken={(token) => onProviderToken?.('google', token)}
          onError={(err) => setLoadError(err?.message || 'Google sign-in failed')}
        />
      ) : null}
      {providers.includes('apple') ? (
        <button
          type="button"
          className="auth-apple-btn w-full min-h-[44px] justify-center cursor-pointer"
          onClick={handleApple}
          disabled={disabled || appleBusy}
          data-testid="social-apple-button"
        >
          {appleBusy ? 'Connecting…' : `${intent === 'signup' ? 'Sign up' : 'Sign in'} with ${PROVIDER_LABELS.apple}`}
        </button>
      ) : null}
      {mode === 'auth' && showDivider && providers.length ? (
        <div className="relative py-2">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-[var(--border-default)]" />
          </div>
          <div className="relative flex justify-center text-[10px] font-mono uppercase tracking-widest">
            <span className="auth-shell-divider-chip px-2 text-[var(--text-tertiary)]">or continue with email</span>
          </div>
        </div>
      ) : null}
    </div>
  );
}
